/**
 * P3-42: ShowChallengeEffectsBtn —— 切换挑战效果详情的按钮。
 *
 * 对齐 `js/challenges.js:1275-1299`：
 * - 仅当至少一个已解锁挑战有可展示 effect 时可见
 * - 根据 `game.detailedChallengeInfo` 切换 show/hide 文案
 * - 点击 toggle boolean
 */
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { GameButton } from "../../components/GameButton";

interface ChallengeLike {
  unlocked?: boolean;
  effects?: Record<string, unknown>;
  totalEffectsCached?: Record<string, number>;
}

function hasVisibleEffects(game: GamePage | null): boolean {
  if (!game?.challenges?.challenges) {
    return false;
  }
  const challenges = game.challenges.challenges as ChallengeLike[];
  for (const ch of challenges) {
    if (!ch.unlocked || !ch.effects) {
      continue;
    }
    for (const effectName in ch.effects) {
      const value = ch.totalEffectsCached?.[effectName] ?? 0;
      const dp = game.getEffectDisplayParams?.(effectName, value, false);
      if (dp) {
        return true;
      }
    }
  }
  return false;
}

interface ShowEffectsSnapshot {
  visible: boolean;
  name: string;
  description: string;
}

function getSnapshot(game: GamePage | null): ShowEffectsSnapshot | null {
  if (!game) {
    return null;
  }
  return {
    visible: hasVisibleEffects(game),
    name: game.detailedChallengeInfo
      ? t("challendge.effects.hide.label")
      : t("challendge.effects.show.label"),
    description: t("challendge.effects.toggle.desc"),
  };
}

function snapEq(a: ShowEffectsSnapshot | null, b: ShowEffectsSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.visible === b.visible &&
    a.name === b.name &&
    a.description === b.description
  );
}

export default function ShowChallengeEffectsBtn() {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot || !snapshot.visible) {
    return null;
  }

  return (
    <GameButton
      name={snapshot.name}
      description={snapshot.description}
      getTooltip={() => snapshot.description}
      onClick={() => {
        const g = getGame();
        if (g) {
          g.detailedChallengeInfo = !g.detailedChallengeInfo;
        }
      }}
    />
  );
}
